<template>
  <oxd-table-filter :filter-title="$t('payroll.my_payslips')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.year"
              :rules="rules.year"
              :label="$t('payroll.year')"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div v-if="showDatatable" class="orangehrm-paper-container">
    <table-header
      :total="total"
      :loading="isLoading"
      :selected="checkedItems.length"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
    <my-payslip-details-dialog
      :show="detailsDialogVisible"
      :payslip-id="selectedPayslipId"
      :payroll-run-name="selectedPayrollRunName"
      :time-period="selectedTimePeriod"
      @close="onCloseDetails"
    />
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';

import {required} from '@/core/util/validation/rules';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import MyPayslipDetailsDialog from '@/orangehrmPayrollPlugin/components/MyPayslipDetailsDialog.vue';
import {openAuthenticatedPayslip} from '@/orangehrmPayrollPlugin/util/openAuthenticatedPayslip';
import {OxdIcon} from '@ohrm/oxd';
import moment from 'moment/moment';

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'my-payslip-details-dialog': MyPayslipDetailsDialog,
  },

  setup() {
    const rules = {
      year: [required],
    };
    const filters = ref({
      year: new Date().getFullYear(),
    });

    const serializedFilters = computed(() => {
      return {
        year: filters.value?.year,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/my-payslips',
    );

    const formatTime = (time) => {
      if (!time) return '';
      const utcTime = moment.utc(time, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
      if (utcTime.format('HH:mm:ss') === '00:00:00') {
        return utcTime.format('YYYY-MM-DD');
      }
      const localTime = utcTime.clone().tz(moment.tz.guess());
      return localTime.format('YYYY-MM-DD HH:mm:ss');
    };

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          payrollRunName: item.payrollRun.name,
          status: item.status,
          generatedAt: formatTime(item.generatedAt.date),
          finalizedAt: formatTime(item.finalizedAt.date),
          currency: item.currency.id,
          downloadUrl: item.downloadUrl.replace('/web', ''),
          notes: item.notes ?? null,
          slipSize: item.slipSize ?? 0,
          timePeriod: `${formatTime(
            item.payrollRun.periodStartDate.date,
          )} - ${formatTime(item.payrollRun.periodEndDate.date)}`,
          payDate: formatTime(item.payrollRun.payDate.date),
        };
      });
    };
    const {total, response, isLoading, execQuery, currentPage, showPaginator} =
      usePaginate(http, {
        query: serializedFilters,
        normalizer: attendanceRecordNormalizer,
        prefetch: false,
      });

    const showDatatable = computed(() => response.value.data !== undefined);

    return {
      http,
      rules,
      total,
      filters,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
      showDatatable,
    };
  },

  data() {
    return {
      detailsDialogVisible: false,
      selectedPayslipId: null,
      selectedPayrollRunName: '',
      selectedTimePeriod: '',
      headers: [
        {
          name: 'payrollRunName',
          slot: 'title',
          title: this.$t('payroll.payroll_run_name'),
          style: {flex: 1},
        },
        {
          name: 'status',
          title: this.$t('general.status'),
          style: {flex: 1, 'text-transform': 'capitalize'},
        },
        {
          name: 'payDate',
          title: this.$t('payroll.pay_date'),
          style: {flex: 1},
        },
        {
          name: 'generatedAt',
          title: this.$t('payroll.generated_at'),
          style: {flex: 1},
        },
        {
          name: 'currency',
          title: this.$t('payroll.currency_id'),
          style: {flex: 1},
        },
        {
          name: 'payslipIssue',
          title: '',
          style: {
            flex: '0 0 2rem',
            'justify-content': 'center',
            'align-items': 'center',
          },
          cellRenderer: this.payslipWarnCellRenderer,
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
    };
  },

  async mounted() {
    this.initializeYearFilter();
    await this.filterItems();
  },

  methods: {
    canViewPayslipSlip(row) {
      const notes = row?.notes;
      if (typeof notes === 'string' && notes.trim() !== '') {
        return false;
      }
      return Number(row?.slipSize ?? 0) > 0;
    },
    payslipUnavailableTitle(row) {
      const notes = row?.notes;
      if (typeof notes === 'string' && notes.trim() !== '') {
        return notes.trim();
      }
      return this.$t('general.warning');
    },
    payslipWarnCellRenderer(...[, , , row]) {
      if (this.canViewPayslipSlip(row)) {
        return {component: 'span'};
      }
      return {
        component: OxdIcon,
        props: {
          name: 'exclamation-triangle',
          title: this.payslipUnavailableTitle(row),
          class: 'payroll-payslip-warn-icon',
        },
      };
    },
    actionRenderer(...[, , , row]) {
      const cellConfig = {
        details: {
          onClick: () => this.onClickDetails(row),
          props: {
            name: 'card-text',
          },
        },
      };
      if (this.canViewPayslipSlip(row)) {
        cellConfig.view = {
          onClick: () => this.onClickView(row),
          props: {
            name: 'file-earmark-text-fill',
          },
        };
      }
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    async onClickView(item) {
      try {
        await openAuthenticatedPayslip(item.downloadUrl);
      } catch {
        this.$toast.error({
          title: this.$t('general.error'),
        });
      }
    },
    onClickDetails(item) {
      this.selectedPayslipId = item.id;
      this.selectedPayrollRunName = item.payrollRunName || '';
      this.selectedTimePeriod = item.timePeriod || '';
      this.detailsDialogVisible = true;
    },
    onCloseDetails() {
      this.detailsDialogVisible = false;
      this.selectedPayslipId = null;
      this.selectedPayrollRunName = '';
      this.selectedTimePeriod = '';
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    parseYearFromUrl() {
      const raw = new URLSearchParams(window.location.search).get('year');
      if (!raw) {
        return null;
      }
      const year = Number.parseInt(raw, 10);
      if (!Number.isInteger(year) || year < 1900 || year > 2100) {
        return null;
      }
      return year;
    },
    initializeYearFilter() {
      this.filters.year = this.parseYearFromUrl() ?? new Date().getFullYear();
    },
    syncYearToUrl() {
      const year = this.filters.year;
      if (year === null || year === undefined || year === '') {
        return;
      }
      const parsed = Number.parseInt(String(year), 10);
      if (!Number.isInteger(parsed) || parsed < 1900 || parsed > 2100) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      params.set('year', String(parsed));
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${
        window.location.hash || ''
      }`;
      window.history.replaceState(window.history.state, '', next);
    },
    async filterItems() {
      this.syncYearToUrl();
      await this.execQuery(false);
    },
  },
};
</script>
<style scoped>
:deep(.payroll-payslip-warn-icon) {
  color: #e67e22;
  font-size: 1rem;
  cursor: default;
  vertical-align: middle;
}
</style>
